import { ChartBarIcon, CrownSimpleIcon, EyeIcon, SparkleIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { useStepFormResume } from "@/client/providers/step-form";

import { AssistantPanel } from "./assistant-panel";
import { AtsPanel } from "./ats-panel";
import { AppearanceToolbar } from "./appearance-toolbar";

type Props = {
  children: ReactNode;
};

type PreviewTab = "preview" | "ats" | "assistant";

const Premium = () => (
  <span className="grid size-4 place-items-center rounded bg-emerald-500 text-white">
    <CrownSimpleIcon size={10} weight="fill" />
  </span>
);

export const BuilderPreview = ({ children }: Props) => {
  const { selector } = useStepFormResume();

  const [tab, setTab] = useState<PreviewTab>("preview");

  useEffect(() => {
    if (selector === "finalization") {
      setTab("assistant");
    }
  }, [selector]);

  return (
    <section className="flex h-full min-h-0 flex-col bg-[#f5f3ff] p-1.5 sm:p-3">
      <div className="flex h-11 shrink-0 items-end justify-start gap-2 overflow-x-auto rounded-t-2xl bg-white px-2 [scrollbar-width:none] sm:justify-center sm:gap-5 sm:px-3">
        <button
          type="button"
          onClick={() => {
            setTab("preview");
          }}
          className={cn(
            "relative flex h-10 shrink-0 items-center gap-1.5 px-1 text-[10px] font-bold sm:text-[11px]",
            tab === "preview" ? "text-indigo-700" : "text-slate-500",
          )}
        >
          <EyeIcon size={16} />
          Aperçu
          {tab === "preview" && (
            <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-indigo-600" />
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            setTab("ats");
          }}
          className={cn(
            "relative flex h-10 shrink-0 items-center gap-1.5 px-1 text-[10px] font-semibold hover:text-indigo-700 sm:text-[11px]",
            tab === "ats" ? "text-indigo-700" : "text-slate-500",
          )}
        >
          <ChartBarIcon size={16} />
          Analyse ATS
          <Premium />
          {tab === "ats" && (
            <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-indigo-600" />
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            setTab("assistant");
          }}
          className={cn(
            "relative flex h-10 shrink-0 items-center gap-1.5 px-1 text-[10px] font-semibold hover:text-indigo-700 sm:text-[11px]",
            tab === "assistant" ? "text-indigo-700" : "text-slate-500",
          )}
        >
          <SparkleIcon size={16} />
          Assistant IA
          <Premium />
          {tab === "assistant" && (
            <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-indigo-600" />
          )}
        </button>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-b-2xl border border-t-0 border-indigo-100 bg-white shadow-[0_10px_30px_rgba(30,27,75,0.08)]">
        {/*
         * L’aperçu reste monté.
         *
         * On le masque visuellement
         * au lieu de détruire l’iframe.
         */}
        <div
          aria-hidden={tab !== "preview"}
          className={cn(
            "absolute inset-0 flex min-h-0 flex-col",
            tab === "preview" ? "visible z-10" : "pointer-events-none invisible z-0",
          )}
        >
          <div className="min-h-0 flex-1 overflow-hidden bg-[#eeecfb] p-1.5 sm:p-3">
            <div className="mx-auto h-full w-full overflow-hidden rounded-md">{children}</div>
          </div>
          <AppearanceToolbar />
        </div>

        {tab === "ats" && (
          <div className="absolute inset-0 z-10 min-h-0">
            <AtsPanel />
          </div>
        )}

        {tab === "assistant" && (
          <div className="absolute inset-0 z-10 min-h-0">
            <AssistantPanel onPreview={() => setTab("preview")} />
          </div>
        )}
      </div>
    </section>
  );
};
